import { useEffect, useRef, useState } from 'react';

export default function HomePage() {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    // Navbar scroll effect
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);

    // Smooth scroll for anchor links
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href^="#"]');
      if (anchor) {
        e.preventDefault();
        const href = anchor.getAttribute('href');
        if (href) {
          const element = document.querySelector(href);
          if (element) {
            const offset = 80;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }
      }
    };

    document.addEventListener('click', handleAnchorClick);

    // Intersection Observer for scroll animations
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
    );

    const elements = document.querySelectorAll('.scroll-animate');
    elements.forEach((el) => observerRef.current?.observe(el));

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleAnchorClick);
      observerRef.current?.disconnect();
    };
  }, []);

  return (
    <div className="bg-[#0A1628] text-white font-sans">
      {/* Navigation Bar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0A1628]/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-[1400px] mx-auto px-8 h-20 flex items-center justify-between">
          {/* Logo */}
          <a href="#hero" className="flex items-center gap-3 cursor-pointer group">
            <div className="w-10 h-10 bg-[#0066FF] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <span className="text-white font-bold text-xl">N</span>
            </div>
            <div className="flex flex-col">
              <span className="text-white font-bold text-lg leading-tight">Novertix</span>
              <span className="text-[#0066FF] text-xs font-semibold leading-tight">OÜ</span>
            </div>
          </a>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#about" className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">About</a>
            <a href="#services" className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Services</a>
            <a href="#team" className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Team</a>
            <a href="#why-us" className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Why Us</a>
            <a href="#testimonials" className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Clients</a>
            <a href="#contact" className="whitespace-nowrap bg-[#0066FF] text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#0052CC] transition-colors cursor-pointer">
              Contact Us
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden text-white text-2xl">
            <i className="ri-menu-line"></i>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/09150cc67f2779dbdbbaa789727e661b.jpg"
            alt="Digital Technology Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628]/90 via-[#0A1628]/70 to-transparent"></div>
        </div>

        {/* Floating Animation Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-[1100px] mx-auto px-8 text-center">
          <div className="scroll-animate opacity-0">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#0066FF] font-semibold mb-10">
              Estonia-based B2B consulting partner for global platforms
            </p>
          </div>

          <div className="scroll-animate opacity-0" style={{ animationDelay: '0.15s' }}>
            <h1 className="text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight mb-8">
              Quietly Powerful<br />Digital Platform Consulting.
            </h1>
          </div>

          <div className="scroll-animate opacity-0" style={{ animationDelay: '0.3s' }}>
            <p className="text-xl text-[#B8C5D6] max-w-[680px] mx-auto leading-relaxed mb-14">
              We support digital product teams with precision — from system architecture and performance optimization to UI/UX and global branding.
            </p>
          </div>

          <div className="scroll-animate opacity-0 flex items-center justify-center gap-6 flex-wrap" style={{ animationDelay: '0.45s' }}>
            <a
              href="#contact"
              className="whitespace-nowrap bg-[#0066FF] text-white px-12 py-4 rounded-[28px] font-semibold text-base shadow-lg shadow-blue-500/30 hover:bg-[#0052CC] transition-all duration-300 hover:shadow-blue-500/50 hover:scale-105 cursor-pointer"
            >
              Book a Consultation
            </a>
            <a
              href="#services"
              className="whitespace-nowrap bg-transparent border-2 border-white text-white px-12 py-4 rounded-[28px] font-semibold text-base hover:bg-white hover:text-[#0A1628] transition-all duration-300 cursor-pointer"
            >
              View Services
            </a>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-3xl text-white/40"></i>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="bg-[#1A2332] py-32 px-8">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid lg:grid-cols-[40%_60%] gap-20 items-start">
            <div className="scroll-animate opacity-0">
              <p className="text-[13px] uppercase tracking-wide text-[#0066FF] font-semibold mb-6">About</p>
              <h2 className="text-5xl font-bold leading-tight">About Novertix OÜ</h2>
            </div>

            <div className="scroll-animate opacity-0" style={{ animationDelay: '0.15s' }}>
              <p className="text-lg text-[#D1D9E6] leading-relaxed mb-6">
                Novertix OÜ is an Estonia-registered professional services company providing remote consulting and operational support for digital platform teams worldwide.
              </p>
              
              <p className="text-lg text-[#D1D9E6] leading-relaxed mb-6">
                Since our establishment, we have partnered with innovative SaaS companies, e-commerce platforms, and international startups across North America, Europe, and Asia-Pacific regions. Our expertise spans the entire digital product lifecycle—from initial architecture planning to post-launch optimization and scaling.
              </p>

              <p className="text-lg text-[#D1D9E6] leading-relaxed mb-12">
                We pride ourselves on being more than just consultants. We become embedded partners in your success, understanding your business objectives deeply and delivering actionable insights that drive measurable results. Our approach combines technical excellence with strategic thinking, ensuring every recommendation aligns with your long-term vision.
              </p>

              <div className="grid md:grid-cols-3 gap-5">
                <div className="bg-[#0A1628] rounded-2xl p-8">
                  <p className="text-xs uppercase text-[#0066FF] font-semibold mb-2 tracking-wide">Registry</p>
                  <p className="text-base text-white font-medium">17300918</p>
                </div>
                <div className="bg-[#0A1628] rounded-2xl p-8">
                  <p className="text-xs uppercase text-[#0066FF] font-semibold mb-2 tracking-wide">Operations</p>
                  <p className="text-base text-white font-medium">Remote consulting delivery</p>
                </div>
                <div className="bg-[#0A1628] rounded-2xl p-8">
                  <p className="text-xs uppercase text-[#0066FF] font-semibold mb-2 tracking-wide">Model</p>
                  <p className="text-base text-white font-medium">Contract-based professional services</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="bg-[#0A1628] py-40 px-8">
        <div className="max-w-[1400px] mx-auto">
          <div className="scroll-animate opacity-0 mb-20">
            <p className="text-[13px] uppercase tracking-wide text-[#0066FF] font-semibold mb-8">Core Services</p>
            <h2 className="text-6xl font-light text-[#8A9BAE] leading-tight mb-2">
              Software, Data, Design
            </h2>
            <h2 className="text-6xl font-bold leading-tight mb-6">
              Consulting Excellence
            </h2>
            <p className="text-lg text-[#B8C5D6] max-w-[560px] leading-relaxed">
              Comprehensive technical and strategic support for digital platforms seeking precision, scalability, and global market readiness.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-10">
            {/* Service Card 1 */}
            <div className="scroll-animate opacity-0 bg-[#1A2332] border border-white/8 rounded-3xl p-12 hover:-translate-y-2 hover:border-[#0066FF]/50 transition-all duration-300 cursor-pointer group" style={{ animationDelay: '0.1s' }}>
              <div className="h-[120px] mb-8 flex items-center justify-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20wireframe%20illustration%20of%20software%20architecture%20with%20connected%20nodes%20and%20data%20flow%20lines%20in%20blue%20gradient%2C%20minimalist%20technical%20diagram%20showing%20system%20structure%20and%20API%20connections%2C%20modern%20digital%20visualization%20with%20clean%20lines%20and%20geometric%20shapes%2C%20professional%20tech%20consulting%20aesthetic%20without%20people&width=400&height=300&seq=service-icon-001&orientation=landscape"
                  alt="Software Development"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="inline-block bg-[#B4FF39] text-[#0A1628] px-4 py-2 rounded-full text-xs uppercase font-bold mb-6">
                Technical
              </div>
              <h3 className="text-3xl font-bold leading-tight mb-5 group-hover:text-[#0066FF] transition-colors">
                Software Development & Technical Consulting
              </h3>
              <p className="text-[15px] text-[#A0AEBF] leading-relaxed mb-6">
                Backend architecture advisory, API flow design, authentication frameworks, scalability review, microservices strategy, cloud infrastructure optimization, and DevOps pipeline enhancement.
              </p>
              <p className="text-[15px] text-[#A0AEBF] leading-relaxed">
                Our senior engineers bring decades of combined experience in building high-performance systems that handle millions of transactions. We help you avoid costly architectural mistakes and accelerate your time-to-market with proven patterns and best practices.
              </p>
            </div>

            {/* Service Card 2 */}
            <div className="scroll-animate opacity-0 bg-[#151E2B] border border-white/8 rounded-3xl p-12 hover:-translate-y-2 hover:border-[#0066FF]/50 transition-all duration-300 cursor-pointer group" style={{ animationDelay: '0.2s' }}>
              <div className="h-[120px] mb-8 flex items-center justify-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20data%20processing%20visualization%20with%20flowing%20streams%20of%20information%20and%20optimization%20patterns%20in%20blue%20gradient%2C%20geometric%20representation%20of%20data%20pipelines%20and%20quality%20control%20workflows%2C%20modern%20technical%20illustration%20with%20clean%20minimalist%20design%20showing%20system%20performance%20metrics%2C%20professional%20consulting%20aesthetic%20without%20people&width=400&height=300&seq=service-icon-002&orientation=landscape"
                  alt="Data Processing"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="inline-block bg-[#B4FF39] text-[#0A1628] px-4 py-2 rounded-full text-xs uppercase font-bold mb-6">
                Optimization
              </div>
              <h3 className="text-3xl font-bold leading-tight mb-5 group-hover:text-[#0066FF] transition-colors">
                Data Processing & System Optimization
              </h3>
              <p className="text-[15px] text-[#A0AEBF] leading-relaxed mb-6">
                Data cleansing workflow advice, QA validation, stability testing, performance tuning support, database query optimization, caching strategies, and load balancing configuration.
              </p>
              <p className="text-[15px] text-[#A0AEBF] leading-relaxed">
                We've helped clients reduce server costs by up to 60% while improving response times by 300%. Our data specialists identify bottlenecks, eliminate redundancies, and implement monitoring systems that ensure your platform runs smoothly at scale.
              </p>
            </div>

            {/* Service Card 3 */}
            <div className="scroll-animate opacity-0 bg-[#1A2332] border border-white/8 rounded-3xl p-12 hover:-translate-y-2 hover:border-[#0066FF]/50 transition-all duration-300 cursor-pointer group" style={{ animationDelay: '0.3s' }}>
              <div className="h-[120px] mb-8 flex items-center justify-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20UI%20UX%20design%20visualization%20with%20interface%20wireframes%20and%20user%20journey%20flow%20diagrams%20in%20blue%20gradient%2C%20geometric%20representation%20of%20digital%20branding%20and%20visual%20design%20systems%2C%20modern%20illustration%20showing%20dashboard%20layouts%20and%20onboarding%20flows%2C%20clean%20minimalist%20professional%20aesthetic%20without%20people&width=400&height=300&seq=service-icon-003&orientation=landscape"
                  alt="Digital Branding"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="inline-block bg-[#B4FF39] text-[#0A1628] px-4 py-2 rounded-full text-xs uppercase font-bold mb-6">
                Design
              </div>
              <h3 className="text-3xl font-bold leading-tight mb-5 group-hover:text-[#0066FF] transition-colors">
                Digital Branding & UI/UX Consulting
              </h3>
              <p className="text-[15px] text-[#A0AEBF] leading-relaxed mb-6">
                UX flow mapping, interface review, visual brand alignment for dashboards and onboarding flows, accessibility compliance, conversion rate optimization, and global service launch support (including the "Friday" platform).
              </p>
              <p className="text-[15px] text-[#A0AEBF] leading-relaxed">
                Our UX strategists have increased client conversion rates by an average of 45% through data-driven design improvements. We create intuitive experiences that users love and that drive business results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="bg-[#1A2332] py-32 px-8">
        <div className="max-w-[1300px] mx-auto">
          <div className="scroll-animate opacity-0 text-center mb-20">
            <p className="text-[13px] uppercase tracking-wide text-[#0066FF] font-semibold mb-7">Why Choose Novertix</p>
            <h2 className="text-5xl font-bold leading-tight mb-10">
              Your Strategic Advantage in Digital Excellence
            </h2>
            <p className="text-lg text-[#D1D9E6] leading-relaxed max-w-[800px] mx-auto">
              We don't just deliver services—we deliver results. Our proven methodology, combined with deep technical expertise and strategic insight, makes us the trusted partner for companies serious about digital success.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="scroll-animate opacity-0 bg-[#0A1628] rounded-2xl p-10" style={{ animationDelay: '0.1s' }}>
              <div className="w-14 h-14 bg-[#0066FF]/10 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-[#0066FF]"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Proven Track Record</h3>
              <p className="text-[15px] text-[#B8C5D6] leading-relaxed">
                Over 50+ successful projects delivered across 15+ countries. Our clients consistently achieve faster time-to-market, reduced operational costs, and improved user satisfaction scores.
              </p>
            </div>

            <div className="scroll-animate opacity-0 bg-[#0A1628] rounded-2xl p-10" style={{ animationDelay: '0.15s' }}>
              <div className="w-14 h-14 bg-[#0066FF]/10 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-team-line text-3xl text-[#0066FF]"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Senior-Level Expertise</h3>
              <p className="text-[15px] text-[#B8C5D6] leading-relaxed">
                No junior consultants. Every team member brings 8+ years of hands-on experience in enterprise-scale projects. You get seasoned professionals who understand complex challenges and deliver sophisticated solutions.
              </p>
            </div>

            <div className="scroll-animate opacity-0 bg-[#0A1628] rounded-2xl p-10" style={{ animationDelay: '0.2s' }}>
              <div className="w-14 h-14 bg-[#0066FF]/10 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-time-line text-3xl text-[#0066FF]"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Rapid Response Time</h3>
              <p className="text-[15px] text-[#B8C5D6] leading-relaxed">
                With team members across multiple time zones, we provide near 24/7 coverage. Critical issues get addressed within hours, not days. Your project momentum never stops.
              </p>
            </div>

            <div className="scroll-animate opacity-0 bg-[#0A1628] rounded-2xl p-10" style={{ animationDelay: '0.25s' }}>
              <div className="w-14 h-14 bg-[#0066FF]/10 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-line-chart-line text-3xl text-[#0066FF]"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">ROI-Focused Approach</h3>
              <p className="text-[15px] text-[#B8C5D6] leading-relaxed">
                Every recommendation is tied to business outcomes. We measure success by your metrics—whether that's reduced infrastructure costs, increased conversion rates, or faster feature deployment.
              </p>
            </div>

            <div className="scroll-animate opacity-0 bg-[#0A1628] rounded-2xl p-10" style={{ animationDelay: '0.3s' }}>
              <div className="w-14 h-14 bg-[#0066FF]/10 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-global-line text-3xl text-[#0066FF]"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Global Market Expertise</h3>
              <p className="text-[15px] text-[#B8C5D6] leading-relaxed">
                We've launched products in 30+ markets worldwide. Our team understands international compliance, localization challenges, and cultural nuances that make or break global expansion.
              </p>
            </div>

            <div className="scroll-animate opacity-0 bg-[#0A1628] rounded-2xl p-10" style={{ animationDelay: '0.35s' }}>
              <div className="w-14 h-14 bg-[#0066FF]/10 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-lock-line text-3xl text-[#0066FF]"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Security & Compliance First</h3>
              <p className="text-[15px] text-[#B8C5D6] leading-relaxed">
                EU-based operations with strict GDPR compliance. We implement security best practices from day one, protecting your data and your customers' trust.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team & Operations Section */}
      <section id="team" className="bg-[#0A1628] py-32 px-8">
        <div className="max-w-[1300px] mx-auto">
          <div className="max-w-[900px] mx-auto text-center">
            <div className="scroll-animate opacity-0">
              <p className="text-[13px] uppercase tracking-wide text-[#0066FF] font-semibold mb-7">Team & Operations</p>
              <h2 className="text-5xl font-bold leading-tight mb-10">
                Multidisciplinary Expertise Across Time Zones
              </h2>
              <p className="text-lg text-[#D1D9E6] leading-relaxed mb-8">
                Our multidisciplinary team includes senior software engineers, data specialists, UX strategists, and technical project coordinators collaborating across time zones.
              </p>
              <p className="text-lg text-[#D1D9E6] leading-relaxed mb-16">
                Each team member is carefully selected for their deep domain expertise, problem-solving abilities, and commitment to excellence. We invest heavily in continuous learning, ensuring our team stays at the forefront of emerging technologies and industry best practices. This dedication to professional growth translates directly into better outcomes for your projects.
              </p>
            </div>

            <div className="scroll-animate opacity-0 text-left mb-14" style={{ animationDelay: '0.15s' }}>
              <h3 className="text-2xl font-semibold mb-8 text-center">Typical Daily Activities Include:</h3>
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-[#0066FF] mt-2 mr-4 flex-shrink-0"></div>
                  <p className="text-base text-[#B8C5D6] leading-relaxed">System architecture and API workflow reviews</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-[#0066FF] mt-2 mr-4 flex-shrink-0"></div>
                  <p className="text-base text-[#B8C5D6] leading-relaxed">UX journey mapping and usability analysis</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-[#0066FF] mt-2 mr-4 flex-shrink-0"></div>
                  <p className="text-base text-[#B8C5D6] leading-relaxed">Backend configuration decision support</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-[#0066FF] mt-2 mr-4 flex-shrink-0"></div>
                  <p className="text-base text-[#B8C5D6] leading-relaxed">Visual branding consistency across platforms</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-[#0066FF] mt-2 mr-4 flex-shrink-0"></div>
                  <p className="text-base text-[#B8C5D6] leading-relaxed">QA testing validation and release readiness assessment</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-[#0066FF] mt-2 mr-4 flex-shrink-0"></div>
                  <p className="text-base text-[#B8C5D6] leading-relaxed">Technical documentation and reporting</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-[#0066FF] mt-2 mr-4 flex-shrink-0"></div>
                  <p className="text-base text-[#B8C5D6] leading-relaxed">Performance monitoring and optimization recommendations</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-[#0066FF] mt-2 mr-4 flex-shrink-0"></div>
                  <p className="text-base text-[#B8C5D6] leading-relaxed">International project scheduling and stakeholder communication</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-[#0066FF] mt-2 mr-4 flex-shrink-0"></div>
                  <p className="text-base text-[#B8C5D6] leading-relaxed">Data pipeline cleansing and quality control workflows</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-[#0066FF] mt-2 mr-4 flex-shrink-0"></div>
                  <p className="text-base text-[#B8C5D6] leading-relaxed">Security audits and vulnerability assessments</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-[#0066FF] mt-2 mr-4 flex-shrink-0"></div>
                  <p className="text-base text-[#B8C5D6] leading-relaxed">Code review and best practices implementation</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-[#0066FF] mt-2 mr-4 flex-shrink-0"></div>
                  <p className="text-base text-[#B8C5D6] leading-relaxed">Strategic planning sessions and roadmap development</p>
                </div>
              </div>
            </div>

            <div className="scroll-animate opacity-0 max-w-[700px] mx-auto bg-[#1A2332] rounded-2xl p-10" style={{ animationDelay: '0.3s' }}>
              <p className="text-xl text-white font-medium leading-relaxed mb-6">
                We operate as long-term consulting partners rather than a transactional agency.
              </p>
              <p className="text-base text-[#B8C5D6] leading-relaxed">
                Our average client relationship spans 18+ months, with many partnerships extending for years. This long-term commitment allows us to deeply understand your business, anticipate challenges, and provide increasingly valuable insights over time. We become an extension of your team, invested in your success as much as you are.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonials Section */}
      <section id="testimonials" className="bg-[#1A2332] py-32 px-8">
        <div className="max-w-[1300px] mx-auto">
          <div className="scroll-animate opacity-0 text-center mb-20">
            <p className="text-[13px] uppercase tracking-wide text-[#0066FF] font-semibold mb-7">Client Success Stories</p>
            <h2 className="text-5xl font-bold leading-tight mb-10">
              What Our Clients Say About Working With Us
            </h2>
            <p className="text-lg text-[#D1D9E6] leading-relaxed max-w-[800px] mx-auto">
              Don't just take our word for it. Here's what industry leaders and innovative companies have to say about their experience partnering with Novertix OÜ.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="scroll-animate opacity-0 bg-[#0A1628] rounded-2xl p-10" style={{ animationDelay: '0.1s' }}>
              <div className="flex items-center gap-1 mb-6">
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
              </div>
              <p className="text-base text-[#D1D9E6] leading-relaxed mb-8 italic">
                "Novertix transformed our backend architecture, reducing our server costs by 55% while improving response times by 280%. Their team identified bottlenecks we didn't even know existed. Absolutely exceptional work."
              </p>
              <div>
                <p className="text-base font-semibold text-white">Michael Chen</p>
                <p className="text-sm text-[#8A9BAE]">CTO, TechFlow SaaS Platform</p>
                <p className="text-xs text-[#6B7A8F] mt-1">San Francisco, USA</p>
              </div>
            </div>

            <div className="scroll-animate opacity-0 bg-[#0A1628] rounded-2xl p-10" style={{ animationDelay: '0.15s' }}>
              <div className="flex items-center gap-1 mb-6">
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
              </div>
              <p className="text-base text-[#D1D9E6] leading-relaxed mb-8 italic">
                "The UX improvements they recommended increased our conversion rate by 47% in just three months. Their data-driven approach and attention to detail made all the difference. Highly recommend for any serious digital product."
              </p>
              <div>
                <p className="text-base font-semibold text-white">Sarah Williams</p>
                <p className="text-sm text-[#8A9BAE]">Head of Product, GlobalCommerce</p>
                <p className="text-xs text-[#6B7A8F] mt-1">London, UK</p>
              </div>
            </div>

            <div className="scroll-animate opacity-0 bg-[#0A1628] rounded-2xl p-10" style={{ animationDelay: '0.2s' }}>
              <div className="flex items-center gap-1 mb-6">
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
              </div>
              <p className="text-base text-[#D1D9E6] leading-relaxed mb-8 italic">
                "Working with Novertix felt like having senior engineers on our team without the overhead. They helped us launch in 8 new markets simultaneously, handling all the technical complexity. True professionals."
              </p>
              <div>
                <p className="text-base font-semibold text-white">Lars Andersson</p>
                <p className="text-sm text-[#8A9BAE]">Founder & CEO, NordicTech AB</p>
                <p className="text-xs text-[#6B7A8F] mt-1">Stockholm, Sweden</p>
              </div>
            </div>

            <div className="scroll-animate opacity-0 bg-[#0A1628] rounded-2xl p-10" style={{ animationDelay: '0.25s' }}>
              <div className="flex items-center gap-1 mb-6">
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
              </div>
              <p className="text-base text-[#D1D9E6] leading-relaxed mb-8 italic">
                "Their data optimization work was game-changing. We went from processing batches overnight to real-time analytics. The performance improvements directly impacted our bottom line. Worth every penny."
              </p>
              <div>
                <p className="text-base font-semibold text-white">Priya Sharma</p>
                <p className="text-sm text-[#8A9BAE]">VP Engineering, DataStream Analytics</p>
                <p className="text-xs text-[#6B7A8F] mt-1">Singapore</p>
              </div>
            </div>

            <div className="scroll-animate opacity-0 bg-[#0A1628] rounded-2xl p-10" style={{ animationDelay: '0.3s' }}>
              <div className="flex items-center gap-1 mb-6">
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
              </div>
              <p className="text-base text-[#D1D9E6] leading-relaxed mb-8 italic">
                "Novertix helped us scale from 10K to 500K users without a single major incident. Their proactive monitoring and optimization recommendations kept us ahead of problems. Couldn't have done it without them."
              </p>
              <div>
                <p className="text-base font-semibold text-white">David Martinez</p>
                <p className="text-sm text-[#8A9BAE]">Co-founder, CloudSync Platform</p>
                <p className="text-xs text-[#6B7A8F] mt-1">Barcelona, Spain</p>
              </div>
            </div>

            <div className="scroll-animate opacity-0 bg-[#0A1628] rounded-2xl p-10" style={{ animationDelay: '0.35s' }}>
              <div className="flex items-center gap-1 mb-6">
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
                <i className="ri-star-fill text-xl text-[#B4FF39]"></i>
              </div>
              <p className="text-base text-[#D1D9E6] leading-relaxed mb-8 italic">
                "The level of expertise and professionalism is unmatched. They don't just solve problems—they teach you why the problem existed and how to prevent it. A true strategic partner for our growth."
              </p>
              <div>
                <p className="text-base font-semibold text-white">Emma Thompson</p>
                <p className="text-sm text-[#8A9BAE]">Product Director, FinTech Innovations</p>
                <p className="text-xs text-[#6B7A8F] mt-1">Toronto, Canada</p>
              </div>
            </div>
          </div>

          <div className="scroll-animate opacity-0 text-center mt-16" style={{ animationDelay: '0.4s' }}>
            <div className="bg-[#0A1628] rounded-2xl p-12 max-w-[900px] mx-auto">
              <div className="grid grid-cols-3 gap-8 mb-8">
                <div>
                  <p className="text-5xl font-bold text-[#0066FF] mb-2">98%</p>
                  <p className="text-sm text-[#B8C5D6]">Client Satisfaction Rate</p>
                </div>
                <div>
                  <p className="text-5xl font-bold text-[#0066FF] mb-2">50+</p>
                  <p className="text-sm text-[#B8C5D6]">Projects Delivered</p>
                </div>
                <div>
                  <p className="text-5xl font-bold text-[#0066FF] mb-2">18+</p>
                  <p className="text-sm text-[#B8C5D6]">Months Avg. Partnership</p>
                </div>
              </div>
              <p className="text-base text-[#D1D9E6] leading-relaxed">
                Join the growing list of successful companies that trust Novertix OÜ for their digital platform needs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section id="compliance" className="bg-[#0A1628] py-28 px-8 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.03) 35px, rgba(255,255,255,.03) 70px)'
          }}></div>
        </div>

        <div className="max-w-[1100px] mx-auto relative z-10">
          <div className="scroll-animate opacity-0 mb-14">
            <p className="text-[13px] uppercase tracking-wide text-[#0066FF] font-semibold mb-8">Compliance & Service Model</p>
            <h2 className="text-5xl font-bold leading-tight">
              Professional Standards & Regulatory Compliance
            </h2>
          </div>

          <div className="scroll-animate opacity-0 space-y-7" style={{ animationDelay: '0.15s' }}>
            <div className="flex items-start max-w-[820px]">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mr-5">
                <i className="ri-checkbox-circle-fill text-2xl text-[#0066FF]"></i>
              </div>
              <p className="text-[17px] text-[#D1D9E6] leading-relaxed">
                All services are provided strictly as professional consulting and development support.
              </p>
            </div>
            <div className="flex items-start max-w-[820px]">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mr-5">
                <i className="ri-checkbox-circle-fill text-2xl text-[#0066FF]"></i>
              </div>
              <p className="text-[17px] text-[#D1D9E6] leading-relaxed">
                All services are delivered digitally with no physical products.
              </p>
            </div>
            <div className="flex items-start max-w-[820px]">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mr-5">
                <i className="ri-checkbox-circle-fill text-2xl text-[#0066FF]"></i>
              </div>
              <p className="text-[17px] text-[#D1D9E6] leading-relaxed">
                No transfer of intellectual property ownership or licensing rights.
              </p>
            </div>
            <div className="flex items-start max-w-[820px]">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mr-5">
                <i className="ri-checkbox-circle-fill text-2xl text-[#0066FF]"></i>
              </div>
              <p className="text-[17px] text-[#D1D9E6] leading-relaxed">
                All work is governed by formal service agreements.
              </p>
            </div>
            <div className="flex items-start max-w-[820px]">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mr-5">
                <i className="ri-checkbox-circle-fill text-2xl text-[#0066FF]"></i>
              </div>
              <p className="text-[17px] text-[#D1D9E6] leading-relaxed font-medium">
                Fully compliant with EU and international service trade regulations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-[#1A2332] py-40 px-8">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-[45%_55%] gap-24 items-start">
            {/* Left Column - Company Info */}
            <div className="scroll-animate opacity-0">
              <p className="text-[13px] uppercase tracking-wide text-[#0066FF] font-semibold mb-7">Get in Touch</p>
              <h2 className="text-6xl font-bold leading-tight mb-12">
                Let's Discuss Your Project
              </h2>

              <div className="space-y-6">
                <div>
                  <p className="text-sm uppercase text-[#0066FF] font-semibold mb-2">Company:</p>
                  <p className="text-lg text-white">Novertix OÜ</p>
                </div>
                <div>
                  <p className="text-sm uppercase text-[#0066FF] font-semibold mb-2">Registry Code:</p>
                  <p className="text-lg text-white">17300918</p>
                </div>
                <div>
                  <p className="text-sm uppercase text-[#0066FF] font-semibold mb-2">Address:</p>
                  <p className="text-base text-[#B8C5D6] leading-relaxed max-w-[320px]">
                    Ruunaoja 3, Lasnamäe District, Tallinn, Harju County, 11415, Estonia
                  </p>
                </div>
                <div>
                  <p className="text-sm uppercase text-[#0066FF] font-semibold mb-2">Email:</p>
                  <a href="mailto:info@cicaus.com" className="text-lg text-[#0066FF] hover:underline cursor-pointer">
                    info@cicaus.com
                  </a>
                </div>
              </div>
            </div>

            {/* Right Column - Contact Form */}
            <div className="scroll-animate opacity-0" style={{ animationDelay: '0.15s' }}>
              <form id="contact-form" data-readdy-form className="bg-[#0A1628] rounded-2xl p-12">
                <div className="space-y-6">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      required
                      className="w-full bg-transparent border-0 border-b border-[#3A4A5C] text-white text-base h-[52px] px-0 focus:outline-none focus:border-[#0066FF] transition-colors placeholder-[#6B7A8F]"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="company"
                      placeholder="Company"
                      required
                      className="w-full bg-transparent border-0 border-b border-[#3A4A5C] text-white text-base h-[52px] px-0 focus:outline-none focus:border-[#0066FF] transition-colors placeholder-[#6B7A8F]"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      required
                      className="w-full bg-transparent border-0 border-b border-[#3A4A5C] text-white text-base h-[52px] px-0 focus:outline-none focus:border-[#0066FF] transition-colors placeholder-[#6B7A8F]"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="Your Message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full bg-transparent border-0 border-b border-[#3A4A5C] text-white text-base px-0 py-3 focus:outline-none focus:border-[#0066FF] transition-colors placeholder-[#6B7A8F] resize-none"
                    ></textarea>
                    <p className="text-xs text-[#6B7A8F] mt-2">Maximum 500 characters</p>
                  </div>
                  <button
                    type="submit"
                    className="whitespace-nowrap w-full bg-[#0066FF] text-white font-semibold text-base h-14 rounded-xl hover:bg-[#0052CC] transition-colors cursor-pointer"
                  >
                    Send Inquiry
                  </button>
                  <div id="form-status" className="text-center text-sm mt-4"></div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A1628] border-t border-white/6 py-16 px-8">
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-sm text-[#6B7A8F]">© 2025 Novertix OÜ. All rights reserved.</p>
          <div className="flex items-center gap-8">
            <a href="#privacy" className="text-sm text-[#8A9BAE] hover:underline transition-all cursor-pointer">
              Privacy Policy
            </a>
            <a href="#terms" className="text-sm text-[#8A9BAE] hover:underline transition-all cursor-pointer">
              Terms of Service
            </a>
            <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-[#8A9BAE] hover:underline transition-all cursor-pointer">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
